<script setup lang="ts">
    import { useShippingStore } from '@/stores/shippingStore';
    import { onMounted, useTemplateRef } from 'vue';
    import { uploadShippingPackage } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import useBuefy from '@/shared/composables/useBuefy';
    import ShippingTable from './ShippingTable.vue';
    import SelectedPackage from './SelectedPackage.vue';
    import CreateExportWizard from './wizard/CreateExportWizard.vue';

    const store = useShippingStore();
    const buefy = useBuefy();
    const { showError } = useShow();
    const fileInputElement = useTemplateRef('packageFileUploadInput');

    function triggerUploadDialog(): void {
        fileInputElement.value?.click();
    }

    async function onUpload(_: Event): Promise<void> {
        const files = fileInputElement.value?.files ?? [];

        if (!files.length)
            return;

        const formData = new FormData();
        formData.append('file', files[0]);

        const loadingComponent = buefy.loading.open({});

        try {
            const response = await uploadShippingPackage(http, formData);
            await store.refresh();
            const newPackage = store.packages.find(x => x.packageId === response.data.packageId);
            if (newPackage) {
                await store.setSelectedPackage(newPackage);
            }
        } catch (err) {
            showError(err);
        } finally {
            loadingComponent.close();
        }
    }

    // BUG: Upload same file twice in a row - only works first time (in Chrome). Our other file uploads also works like this.

    onMounted(store.init);

</script>

<template>
    <div class="columns">
        <div class="column">
            <h1 class="title">
                <b-icon
                    icon="sail-boat"
                    size="" />
                Definition and data shipping
            </h1>
            <h1 class="subtitle">
                Transfer libraries between Common Library environments
            </h1>
        </div>
        <div class="column is-flex is-justify-content-end is-align-items-end">
            <div
                v-show="store.showPackageList"
                class="buttons">
                <b-button
                    type="is-info"
                    icon-left="auto-fix"
                    @click="store.startExportWizard">
                    Create new export
                </b-button>
                <input
                    ref="packageFileUploadInput"
                    type="file"
                    accept=".zip"
                    hidden
                    @change="onUpload">
                <b-button
                    type="is-info"
                    icon-left="upload"
                    @click="triggerUploadDialog">
                    Upload
                </b-button>
            </div>
        </div>
    </div>
    <hr>
    <selected-package />
    <shipping-table />
    <create-export-wizard />
</template>

<style>
.shipping-color-ok {
    color: green;
}
.shipping-color-failure {
    color: red;
}
</style>
